import { fmt } from "@/lib/api";

export const SectionHeader = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div className="shrink-0 border-b border-border bg-card px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={testId}>
    {Icon && <span className="h-8 w-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></span>}
    <div className="min-w-0">
      <h2 className="font-head font-bold text-base md:text-lg tracking-tight uppercase leading-tight">{title}</h2>
      {subtitle && <p className="text-[11px] text-muted-foreground leading-tight">{subtitle}</p>}
    </div>
    <div className="flex-1" />
    <div className="flex items-center gap-1.5 flex-wrap">{children}</div>
  </div>
);

export const Toolbar = ({ children, testId }) => (
  <div className="exec-toolbar" data-testid={testId}>{children}</div>
);

export const ToolGroup = ({ label, children }) => (
  <div className="exec-toolgroup">
    <div className="flex items-center gap-1 flex-wrap">{children}</div>
    <span className="exec-toolgroup-label">{label}</span>
  </div>
);

export const Panel = ({ title, icon: Icon, children, className = "", right, testId, tone }) => (
  <div className={`exec-panel ${tone === "primary" ? "border-primary/40" : tone === "copper" ? "border-copper/50" : ""} ${className}`} data-testid={testId}>
    {title && (
      <div className={`exec-panel-head ${tone === "primary" ? "!text-primary" : tone === "copper" ? "!text-copper" : ""}`}>
        {Icon && <Icon className="h-3.5 w-3.5" />} {title}
        <div className="flex-1" />
        {right}
      </div>
    )}
    {children}
  </div>
);

export const KpiCard = ({ label, value, unit, hint, accent, icon: Icon, testId, money }) => (
  <div className="exec-kpi exec-rise" style={{ "--kpi-accent": accent }} data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      {Icon && <Icon className="h-4 w-4 opacity-60" style={{ color: accent }} />}
    </div>
    <div className="font-mono-num text-xl md:text-2xl font-semibold mt-2 tracking-tight" data-testid={testId ? `${testId}-value` : undefined}>
      {money && <span className="text-sm mr-1 opacity-60">$</span>}{typeof value === "number" ? fmt(value, money ? 2 : 0) : value}
      {unit && <span className="text-xs ml-1 text-muted-foreground font-sans">{unit}</span>}
    </div>
    {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testId }) => (
  <div className="m-6 border-2 border-dashed border-border rounded-md bg-muted/20 p-10 text-center exec-rise" data-testid={testId}>
    {Icon && <Icon className="h-9 w-9 mx-auto text-muted-foreground/60" />}
    <h3 className="font-head font-bold uppercase text-sm mt-3">{title}</h3>
    {text && <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const BADGE = {
  success: "bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800",
  warning: "bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800",
  danger: "bg-red-100 dark:bg-red-950/80 text-red-800 dark:text-red-300 border-red-300 dark:border-red-800",
  neutral: "bg-muted text-muted-foreground border-border",
  primary: "bg-primary/10 text-primary border-primary/30",
};
export const Badge = ({ tone = "neutral", children, className = "", testId }) => (
  <span className={`inline-flex items-center gap-1 border text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase tracking-wide ${BADGE[tone]} ${className}`} data-testid={testId}>{children}</span>
);

export const SummaryBar = ({ items, testId }) => (
  <div className="exec-summary-bar" data-testid={testId}>
    {items.map((it) => (
      <div key={it.label} className={`exec-summary-item ${it.highlight ? "bg-white/5" : ""}`} data-testid={it.testId}>
        <span className="lbl">{it.label}</span>
        <span className={`val ${it.highlight ? "!text-amber-400 !text-base font-bold" : ""}`}>{it.value}</span>
      </div>
    ))}
  </div>
);
